/**
 * Guidance by earnings season (issue #387): how many of the calls on record raised, kept or
 * lowered their outlook in the most recent reporting season.
 *
 * ONE definition for three consumers — the worker route (`earnings-transcript-cron`
 * `/guidance-season`, which buckets the rows), the desktop Market Analysis "Guidance" card and
 * the mobile Brief card (which pick a season and word it). It lives in `src/shared/` because
 * the dependency direction is forced: CRA cannot import from outside `src/`, but wrangler can
 * import into it — same arrangement as `bandSignals.js` and `callHighlightStyles.js`.
 *
 * A "season" is the CALENDAR quarter the calls were held in, named after the fiscal quarter most
 * of them reported on (calls held Jul–Sep are "the Q2 earnings season"). The season a call
 * belongs to is decided on its New York session date, never its UTC date: an after-the-close
 * call on Sep 30 carries an Oct 1 UTC `call_date` and must still count in the Jul–Sep season.
 *
 * Which season is "last" is NOT gated on today's date: a few days after a quarter boundary the
 * current quarter holds ~0 calls, so `pickSeason` takes the newest season that reached
 * MIN_SEASON_CALLS and falls back to the one before it. No season with enough calls → null,
 * and both surfaces render nothing rather than an empty card.
 *
 * All user-facing wording is in this file, in plain language.
 */
import { easternSessionDate } from '../utils/earningsElapsed';

/** Fewer calls than this and a season is not a read, it's an anecdote. */
export const MIN_SEASON_CALLS = 5;

/** The headline three, in the order the bar draws them. */
export const SEASON_DIRECTIONS = ['raised', 'maintained', 'lowered'];
/** Counted and noted, but outside the raised/kept/lowered ratio. */
export const OTHER_DIRECTIONS = ['initiated', 'none'];
const ALL_DIRECTIONS = [...SEASON_DIRECTIONS, ...OTHER_DIRECTIONS];

/** Plain words for each direction — "kept", not "maintained", on every surface. */
export const DIRECTION_WORDS = {
  raised: 'raised',
  maintained: 'kept',
  lowered: 'lowered',
  initiated: 'gave guidance for the first time',
  none: 'gave no guidance',
};

/** Where the counts come from. Every surface says this, or the read passes for market breadth. */
export const SEASON_NOTE =
  "Counts the earnings calls on record for stocks on users' watchlists — not the whole market.";

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');

/**
 * The season a call belongs to, from its UTC ISO `call_date`. Null for an unparseable date.
 * `key` sorts chronologically as a string ("2026Q3").
 */
export function seasonOf(callDate) {
  const session = easternSessionDate(callDate);
  if (!session) return null;
  const year = Number(session.slice(0, 4));
  const month = Number(session.slice(5, 7));
  const q = Math.floor((month - 1) / 3); // 0-3, the calendar quarter the call was held in
  const firstMonth = q * 3 + 1;
  const lastDay = [31, 30, 30, 31][q];
  // Calls held in calendar quarter N report on fiscal quarter N-1 (Jul–Sep calls = "Q2 season").
  const reported = q === 0 ? { q: 4, year: year - 1 } : { q, year };
  return {
    key: `${year}Q${q + 1}`,
    from: `${year}-${pad(firstMonth)}-01`,
    to: `${year}-${pad(firstMonth + 2)}-${lastDay}`,
    label: `Q${reported.q} ${reported.year} earnings season`,
    span: `${MONTHS[firstMonth - 1]}–${MONTHS[firstMonth + 1]} ${year}`,
  };
}

const emptyCounts = () => Object.fromEntries(ALL_DIRECTIONS.map((d) => [d, 0]));

/**
 * Buckets finished calls into seasons, newest first.
 *
 * @param rows `{ ticker, call_date, direction, name? }` — one per finished call. A ticker with two
 *   rows in one season (a re-listed article) counts once, on its newest call.
 * @param now  the moment the read is taken (Date); decides `inProgress`
 * @returns [{ key, label, span, from, to, inProgress, calls, counts, tickers }]
 */
export function bucketSeasons(rows, { now = new Date() } = {}) {
  const today = easternSessionDate(now.toISOString());
  const byKey = new Map();
  for (const row of rows || []) {
    const season = seasonOf(row?.call_date);
    if (!season) continue;
    const direction = ALL_DIRECTIONS.includes(row.direction) ? row.direction : null;
    if (!direction) continue; // a call the extraction could not classify is not a vote
    if (!byKey.has(season.key)) byKey.set(season.key, { ...season, latest: new Map() });
    const latest = byKey.get(season.key).latest;
    const prev = latest.get(row.ticker);
    if (!prev || String(row.call_date) > String(prev.call_date)) latest.set(row.ticker, row);
  }

  return [...byKey.values()]
    .sort((a, b) => (a.key < b.key ? 1 : -1))
    .map(({ latest, ...season }) => {
      const counts = emptyCounts();
      const tickers = Object.fromEntries(ALL_DIRECTIONS.map((d) => [d, []]));
      const calls = [...latest.values()].sort((a, b) => (a.call_date < b.call_date ? 1 : -1));
      for (const call of calls) {
        counts[call.direction] += 1;
        tickers[call.direction].push({
          ticker: call.ticker,
          name: typeof call.name === 'string' && call.name ? call.name : null,
          industry: typeof call.industry === 'string' && call.industry ? call.industry : null,
          callDate: call.call_date,
        });
      }
      return {
        ...season,
        inProgress: Boolean(today) && today <= season.to,
        calls: calls.length,
        counts,
        tickers,
      };
    });
}

/** An industry needs this many calls before it gets its own row; fewer and it is "other". */
export const MIN_INDUSTRY_CALLS = 3;
/** At most this many industry rows, largest first — the floor above keeps it short until then. */
export const MAX_INDUSTRY_ROWS = 6;
export const OTHER_INDUSTRIES_LABEL = 'Other industries';

/**
 * The season's raised/kept/lowered split per industry, for the expanded desktop card.
 *
 * Only industries with at least `minCalls` calls get a row, largest first, at most `maxRows`;
 * everything else (small industries and calls with no industry on record) is folded into ONE
 * "Other industries" row so the list cannot grow into a wall as coverage grows. Calls outside
 * the ratio (`initiated`/`none`) count toward an industry's size but draw nothing. Empty when
 * no industry clears the floor — one "Other" row alone would just restate the season bar.
 *
 * @returns [{ industry, calls, counts, other }] — `other: true` on the folded row
 */
export function industryBreakdown(season, { minCalls = MIN_INDUSTRY_CALLS, maxRows = MAX_INDUSTRY_ROWS } = {}) {
  const groups = new Map();
  for (const direction of ALL_DIRECTIONS) {
    for (const entry of season?.tickers?.[direction] || []) {
      const key = entry.industry || OTHER_INDUSTRIES_LABEL;
      if (!groups.has(key)) groups.set(key, { industry: key, calls: 0, counts: emptyCounts() });
      const g = groups.get(key);
      g.calls += 1;
      g.counts[direction] += 1;
    }
  }
  const named = [...groups.values()]
    .filter((g) => g.industry !== OTHER_INDUSTRIES_LABEL && g.calls >= minCalls)
    .sort((a, b) => b.calls - a.calls || a.industry.localeCompare(b.industry));
  if (!named.length) return [];
  const rows = named.slice(0, maxRows);
  const other = { industry: OTHER_INDUSTRIES_LABEL, calls: 0, counts: emptyCounts(), other: true };
  for (const g of groups.values()) {
    if (rows.includes(g)) continue;
    other.calls += g.calls;
    for (const d of ALL_DIRECTIONS) other.counts[d] += g.counts[d];
  }
  return other.calls > 0 ? [...rows, other] : rows;
}

/**
 * The season to show: the newest with at least `minCalls` calls. Null when none qualifies —
 * the caller renders nothing.
 */
export function pickSeason(seasons, { minCalls = MIN_SEASON_CALLS } = {}) {
  if (!Array.isArray(seasons)) return null;
  return seasons.find((s) => Number.isFinite(s?.calls) && s.calls >= minCalls) || null;
}

/** "Q2 2026 earnings season" or "Q2 2026 earnings season so far" while it is still running. */
export function seasonTitle(season) {
  return season.inProgress ? `${season.label} so far` : season.label;
}

/**
 * The one-line takeaway. Decided on the raised/kept/lowered calls only — a call that gave no
 * guidance is not a vote either way.
 */
export function seasonHeadline(season) {
  const { raised = 0, maintained = 0, lowered = 0 } = season?.counts || {};
  const decided = raised + maintained + lowered;
  if (decided <= 0) return 'No company gave an outlook';
  if (raised / decided >= 0.5) return 'Most companies raised their outlook';
  if (lowered / decided >= 0.5) return 'Most companies lowered their outlook';
  if (maintained / decided >= 0.5) return 'Most companies kept their outlook';
  if (raised > lowered) return 'More companies raised their outlook than lowered it';
  if (lowered > raised) return 'More companies lowered their outlook than raised it';
  return 'As many companies raised their outlook as lowered it';
}

/** "14 raised · 9 kept · 1 lowered" — the headline three, always all three, in bar order. */
export function seasonCountsLine(season) {
  return SEASON_DIRECTIONS.map((d) => `${season.counts[d]} ${DIRECTION_WORDS[d]}`).join(' · ');
}

/** "2 gave guidance for the first time, 1 gave no guidance" — only the groups that occurred. */
export function seasonOthersLine(season) {
  return OTHER_DIRECTIONS.filter((d) => season.counts[d] > 0)
    .map((d) => `${season.counts[d]} ${DIRECTION_WORDS[d]}`)
    .join(', ');
}

/** "Across 25 calls held Jul–Sep 2026." */
export function seasonScopeLine(season) {
  return `Across ${season.calls} ${season.calls === 1 ? 'call' : 'calls'} held ${season.span}.`;
}
